import debounce from "lodash/debounce";
import { AuditActionTypes } from "../ts/enums";
import { NotificationType } from "../controllers/messaging";
import {
  AuditMessage,
  UserActionAuditMessage,
  IControl,
  ISettingsItem,
  IShowNotificationState
} from "../ts/Definitions";
import { getItemTypeDisplayName } from "../utilities/utils";

const MAX_STRING_LENGTH = 140;
const MAX_ARRAY_ITEMS = 8;
const STRING_DIFF_CONTEXT_CHARS = 64;
const STRING_DIFF_CHANGED_CHARS = 56;

interface IAuditDatabase {
  auditUser: (messages: AuditMessage[], control: IControl) => void;
}

export class AuditService {
  private auditQueue: AuditMessage[] = [];
  private database: IAuditDatabase;
  private getControl: () => IControl;
  private showMessage: (args: IShowNotificationState) => void;
  private pushAuditMessages: () => void;

  constructor(
    database: IAuditDatabase,
    getControl: () => IControl,
    showMessage: (args: IShowNotificationState) => void
  ) {
    this.database = database;
    this.getControl = getControl;
    this.showMessage = showMessage;
    this.pushAuditMessages = debounce(this.pushAuditMessagesInternal, 1000);
  }

  /**
   * Queue an audit message to be sent to the database
   */
  public audit(message: AuditMessage): void {
    this.auditQueue.push(message);
    this.pushAuditMessages();
  }

  /**
   * Add a user action audit entry
   */
  public auditUserAction(action: string, actionType: AuditActionTypes): void {
    this.audit(
      new UserActionAuditMessage(actionType, this.getControl().username, action)
    );
  }

  /**
   * Audit owner item changes with optional diff
   */
  public auditOwnerItemAction(
    action: string,
    diffOrBefore?: Record<string, any>,
    after?: Record<string, any>
  ): void {
    this.showMessage({
      message: action,
      notificationType: NotificationType.ScheduleInfo,
    });

    let auditAction = action;
    const diffText = after !== undefined
      ? this.formatValueDiff(diffOrBefore, after)
      : this.formatObjectDiff(diffOrBefore);
    if (diffText) {
      auditAction = action.concat(`\n${diffText}`);
    }

    this.audit(
      new UserActionAuditMessage(AuditActionTypes.Settings, "Admin", auditAction)
    );
  }

  /**
   * Audit settings item changes (add/update)
   */
  public auditSettingsItem(
    item: ISettingsItem,
    oldItem: ISettingsItem | undefined,
    action: string
  ): void {
    const itemName = `${getItemTypeDisplayName(item.itemType)} ${item.name}`;
    let auditAction = `${action} ${itemName}`;

    if (oldItem) {
      const diffText = this.formatValueDiff(oldItem, item);
      if (diffText) {
        auditAction = auditAction.concat(`\n${diffText}`);
      }
    }

    this.audit(
      new UserActionAuditMessage(
        AuditActionTypes.Settings,
        this.getControl().username,
        auditAction
      )
    );
  }

  /**
   * Audit settings item deletion
   */
  public auditDeleteItem(item: ISettingsItem, action: string): void {
    const itemName = `${getItemTypeDisplayName(item.itemType)} ${item.name}`;
    this.audit(
      new UserActionAuditMessage(
        AuditActionTypes.Settings,
        this.getControl().username,
        `${action} ${itemName}`
      )
    );
  }

  /**
   * Audit bank balance changes
   */
  public auditBankChange(reason: string, change: number, newBalance: number): number {
    this.audit(
      new UserActionAuditMessage(
        AuditActionTypes.Session,
        this.getControl().username,
        `${reason} ${change > 0 ? "+" : ""}${change} (Balance: ${newBalance})`
      )
    );
    return newBalance;
  }

  /**
   * Clear the audit queue (for testing or reset purposes)
   */
  public clearQueue(): void {
    this.auditQueue = [];
  }

  /**
   * Cancel any pending debounced audit pushes
   */
  public dispose(): void {
    if (this.pushAuditMessages && typeof this.pushAuditMessages === 'function') {
      (this.pushAuditMessages as any).cancel?.();
    }
    this.clearQueue();
  }

  private pushAuditMessagesInternal(): void {
    if (this.auditQueue.length > 0) {
      this.database.auditUser(this.auditQueue, this.getControl());
      this.auditQueue = [];
    }
  }

  private formatObjectDiff(value: Record<string, any> | undefined, path = ""): string {
    if (!value || Object.keys(value).length === 0) return "";
    const lines = this.walkDiff(undefined, value, path);
    return lines.slice(0, 24).join("\n");
  }

  private formatValueDiff(before: unknown, after: unknown, path = ""): string {
    const lines = this.walkDiff(before, after, path);
    return lines.slice(0, 24).join("\n");
  }

  private walkDiff(before: unknown, after: unknown, path: string): string[] {
    if (this.valuesEqual(before, after)) return [];

    if (this.isPrimitive(before) || this.isPrimitive(after)) {
      return [this.formatLeaf(path, before, after)];
    }

    if (Array.isArray(before) || Array.isArray(after)) {
      return this.walkArrayDiff(before as unknown[] | undefined, after as unknown[] | undefined, path);
    }

    if (this.isPlainObject(before) || this.isPlainObject(after)) {
      return this.walkObjectDiff(before as Record<string, unknown> | undefined, after as Record<string, unknown> | undefined, path);
    }

    return [this.formatLeaf(path, before, after)];
  }

  private walkObjectDiff(before: Record<string, unknown> | undefined, after: Record<string, unknown> | undefined, path: string): string[] {
    const beforeKeys = Object.keys(before ?? {});
    const afterKeys = Object.keys(after ?? {});
    const keys = new Set([...beforeKeys, ...afterKeys]);
    const lines: string[] = [];

    keys.forEach((key) => {
      const childPath = path ? `${path}.${key}` : key;
      lines.push(...this.walkDiff(before?.[key], after?.[key], childPath));
    });

    return lines;
  }

  private walkArrayDiff(before: unknown[] | undefined, after: unknown[] | undefined, path: string): string[] {
    const beforeList = before ?? [];
    const afterList = after ?? [];
    if (beforeList.length === 0 && afterList.length === 0) return [];

    const beforeByKey = this.indexArrayByKey(beforeList);
    const afterByKey = this.indexArrayByKey(afterList);
    if (beforeByKey && afterByKey) {
      const lines: string[] = [];
      const keys = new Set([...Object.keys(beforeByKey), ...Object.keys(afterByKey)]);
      keys.forEach((key) => {
        const childPath = `${path}[${key}]`;
        lines.push(...this.walkDiff(beforeByKey[key], afterByKey[key], childPath));
      });
      if (lines.length > 0) return lines;
    }

    if (beforeList.length > MAX_ARRAY_ITEMS || afterList.length > MAX_ARRAY_ITEMS) {
      return [this.formatLeaf(path, `${beforeList.length} item(s)`, `${afterList.length} item(s)`).replace(/^: /, "")];
    }

    const maxItems = Math.max(beforeList.length, afterList.length);
    const lines: string[] = [];
    for (let idx = 0; idx < maxItems; idx += 1) {
      lines.push(...this.walkDiff(beforeList[idx], afterList[idx], `${path}[${idx}]`));
    }
    if (lines.length > 0) return lines;

    return [this.formatLeaf(path, this.formatInlineValue(beforeList), this.formatInlineValue(afterList))];
  }

  private indexArrayByKey(items: unknown[]): Record<string, unknown> | undefined {
    if (items.length === 0) return {};
    const keyed: Record<string, unknown> = {};
    for (const item of items) {
      if (!this.isPlainObject(item)) return undefined;
      const key = (item as Record<string, unknown>).key;
      if (typeof key !== "string" || key.trim().length === 0) return undefined;
      if (Object.prototype.hasOwnProperty.call(keyed, key)) return undefined;
      keyed[key] = item;
    }
    return keyed;
  }

  private formatLeaf(path: string, before: unknown, after: unknown): string {
    const label = path ? `${path}: ` : "";
    if (typeof before === "string" && typeof after === "string") {
      return `${label}${this.formatContextualStringDiff(before, after)}`;
    }
    return `${label}${this.formatInlineValue(before)} → ${this.formatInlineValue(after)}`;
  }

  private formatContextualStringDiff(before: string, after: string): string {
    const left = this.normalizeStringForDiff(before);
    const right = this.normalizeStringForDiff(after);
    if (left === right) return JSON.stringify(left);

    let prefixLength = this.getCommonPrefixLength(left, right);
    let suffixLength = this.getCommonSuffixLength(left, right, prefixLength);
    ({ prefixLength, suffixLength } = this.expandDiffToWordBoundaries(left, right, prefixLength, suffixLength));
    const beforeChanged = left.slice(prefixLength, left.length - suffixLength);
    const afterChanged = right.slice(prefixLength, right.length - suffixLength);

    const beforeContextStart = Math.max(0, prefixLength - STRING_DIFF_CONTEXT_CHARS);
    const afterContextEnd = Math.min(right.length, right.length - suffixLength + STRING_DIFF_CONTEXT_CHARS);
    const prefixContext = left.slice(beforeContextStart, prefixLength);
    const suffixContext = right.slice(right.length - suffixLength, afterContextEnd);

    const prefixEllipsis = beforeContextStart > 0 ? "... " : "";
    const suffixEllipsis = afterContextEnd < right.length ? " ..." : "";

    const removed = this.truncateMiddle(beforeChanged, STRING_DIFF_CHANGED_CHARS).trim();
    const added = this.truncateMiddle(afterChanged, STRING_DIFF_CHANGED_CHARS).trim();

    let change = "";
    if (removed && added) {
      change = `~${removed}~ → **${added}**`;
    } else if (removed) {
      change = `~${removed}~`;
    } else if (added) {
      change = `**${added}**`;
    }

    const snippet = `${prefixEllipsis}${prefixContext}${change}${suffixContext}${suffixEllipsis}`.replace(/\s+/g, " ").trim();
    return JSON.stringify(snippet);
  }

  private normalizeStringForDiff(value: string): string {
    return value.replace(/\s+/g, " ").trim();
  }

  private getCommonPrefixLength(left: string, right: string): number {
    const limit = Math.min(left.length, right.length);
    let idx = 0;
    while (idx < limit && left[idx] === right[idx]) {
      idx += 1;
    }
    return idx;
  }

  private getCommonSuffixLength(left: string, right: string, prefixLength: number): number {
    const leftRemaining = left.length - prefixLength;
    const rightRemaining = right.length - prefixLength;
    const limit = Math.min(leftRemaining, rightRemaining);
    let idx = 0;
    while (idx < limit && left[left.length - 1 - idx] === right[right.length - 1 - idx]) {
      idx += 1;
    }
    return idx;
  }

  private expandDiffToWordBoundaries(
    left: string,
    right: string,
    prefixLength: number,
    suffixLength: number
  ): { prefixLength: number; suffixLength: number } {
    let prefix = prefixLength;
    let suffix = suffixLength;

    while (
      prefix > 0
      && prefix < left.length
      && prefix < right.length
      && this.isWordChar(left[prefix - 1])
      && this.isWordChar(left[prefix])
      && this.isWordChar(right[prefix])
    ) {
      prefix -= 1;
    }

    while (suffix > 0) {
      const leftSuffixStart = left.length - suffix;
      const rightSuffixStart = right.length - suffix;
      const leftPrev = left[leftSuffixStart - 1];
      const rightPrev = right[rightSuffixStart - 1];
      const leftFirstSuffix = left[leftSuffixStart];
      const rightFirstSuffix = right[rightSuffixStart];

      if (
        this.isWordChar(leftFirstSuffix)
        && this.isWordChar(rightFirstSuffix)
        && (this.isWordChar(leftPrev) || this.isWordChar(rightPrev))
      ) {
        suffix -= 1;
        continue;
      }

      break;
    }

    return { prefixLength: prefix, suffixLength: suffix };
  }

  private isWordChar(value: string | undefined): boolean {
    if (!value) return false;
    return /[A-Za-z0-9_]/.test(value);
  }

  private truncateMiddle(value: string, maxLength: number): string {
    if (value.length <= maxLength) return value;
    if (maxLength <= 1) return value.slice(0, maxLength);
    const startLength = Math.ceil((maxLength - 1) / 2);
    const endLength = Math.floor((maxLength - 1) / 2);
    return `${value.slice(0, startLength)}…${value.slice(value.length - endLength)}`;
  }

  private formatInlineValue(value: unknown): string {
    if (value === undefined) return "<missing>";
    if (value === null) return "null";
    if (typeof value === "string") {
      const collapsed = value.replace(/\s+/g, " ").trim();
      if (collapsed.length <= MAX_STRING_LENGTH) return JSON.stringify(collapsed);
      return JSON.stringify(`${collapsed.slice(0, MAX_STRING_LENGTH - 1)}…`);
    }
    if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
      return String(value);
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (Array.isArray(value)) {
      return `[${value.length} item(s)]`;
    }
    if (this.isPlainObject(value)) {
      const keys = Object.keys(value as Record<string, unknown>);
      return keys.length === 0 ? "{}" : `{${keys.slice(0, 4).join(",")}${keys.length > 4 ? ",…" : ""}}`;
    }
    return JSON.stringify(value);
  }

  private valuesEqual(before: unknown, after: unknown): boolean {
    if (before === after) return true;
    if (before instanceof Date && after instanceof Date) return before.getTime() === after.getTime();
    if (Array.isArray(before) && Array.isArray(after)) {
      if (before.length !== after.length) return false;
      for (let idx = 0; idx < before.length; idx += 1) {
        if (!this.valuesEqual(before[idx], after[idx])) return false;
      }
      return true;
    }
    if (this.isPlainObject(before) && this.isPlainObject(after)) {
      const beforeKeys = Object.keys(before);
      const afterKeys = Object.keys(after);
      if (beforeKeys.length !== afterKeys.length) return false;
      for (const key of beforeKeys) {
        if (!Object.prototype.hasOwnProperty.call(after, key)) return false;
        if (!this.valuesEqual((before as Record<string, unknown>)[key], (after as Record<string, unknown>)[key])) return false;
      }
      return true;
    }
    return false;
  }

  private isPrimitive(value: unknown): boolean {
    return value === null || value === undefined || typeof value !== "object";
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date);
  }
}
